import { useState } from 'react';
import type { Tournament, Match } from '../types';
import { submitScore } from '../services/api';

interface ScoreEntryPageProps {
  tournament: Tournament;
  onUpdate: (updates: Partial<Tournament>) => void;
}

export default function ScoreEntryPage({ tournament, onUpdate }: ScoreEntryPageProps) {
  const [activeMatch, setActiveMatch] = useState<Match | null>(null);
  const [activeRoundIndex, setActiveRoundIndex] = useState<number>(0);
  const [teamAScore, setTeamAScore] = useState('');
  const [teamBScore, setTeamBScore] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const getPlayerName = (id: string) => {
    const player = tournament.players.find(p => p.id === id);
    return player?.name || 'Unknown';
  };

  const getRoundProgress = (roundIndex: number) => {
    const round = tournament.schedule[roundIndex];
    const completed = round.matches.filter(m => m.isComplete).length;
    return `${completed}/${round.matches.length}`;
  };

  const openScoreModal = (match: Match, roundIndex: number) => {
    setActiveMatch(match);
    setActiveRoundIndex(roundIndex);
    setTeamAScore(match.teamAScore?.toString() || '');
    setTeamBScore(match.teamBScore?.toString() || '');
    setError(null);
  };

  const closeScoreModal = () => {
    setActiveMatch(null);
    setTeamAScore('');
    setTeamBScore('');
    setError(null);
  };

  const handleSubmitScore = async () => {
    if (!activeMatch) return;

    const scoreA = parseInt(teamAScore);
    const scoreB = parseInt(teamBScore);

    if (isNaN(scoreA) || isNaN(scoreB) || scoreA < 0 || scoreB < 0) {
      setError('Please enter valid scores');
      return;
    }

    if (scoreA === scoreB) {
      setError('Scores cannot be tied');
      return;
    }

    // Check win by requirement
    const winningScore = Math.max(scoreA, scoreB);
    const losingScore = Math.min(scoreA, scoreB);
    const scoreDiff = winningScore - losingScore;

    if (winningScore < tournament.config.targetScore) {
      setError(`Winning score must be at least ${tournament.config.targetScore}`);
      return;
    }

    if (winningScore > tournament.config.targetScore && scoreDiff < tournament.config.winBy) {
      setError(`Must win by ${tournament.config.winBy} points`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await submitScore(
        tournament,
        activeMatch.roundIndex,
        activeMatch.id,
        scoreA,
        scoreB
      );

      // Update the tournament schedule
      onUpdate({ schedule: result.schedule });

      // Also update player stats based on the new leaderboard
      const playerMap = new Map(tournament.players.map(p => [p.id, p]));
      result.leaderboard.forEach(entry => {
        const player = playerMap.get(entry.playerId);
        if (player) {
          player.wins = entry.wins;
          player.totalPoints = entry.totalPoints;
        }
      });
      onUpdate({ players: Array.from(playerMap.values()) });

      closeScoreModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit score');
    } finally {
      setSubmitting(false);
    }
  };

  const totalCompleted = tournament.schedule.reduce((acc, round) => 
    acc + round.matches.filter(m => m.isComplete).length, 0
  );
  const totalMatches = tournament.schedule.reduce((acc, round) => 
    acc + round.matches.length, 0
  );

  return (
    <div>
      <h1>Score Entry</h1>

      {error && !activeMatch && <div className="error">{error}</div>}

      {/* Tournament Progress Bar */}
      <div className="card mb-2">
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>Tournament Progress:</strong> {totalCompleted}/{totalMatches} matches completed
        </div>
        <div style={{ 
          background: '#e0e0e0', 
          borderRadius: '4px', 
          height: '20px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: totalCompleted === totalMatches ? '#27ae60' : '#3498db',
            height: '100%',
            width: `${totalMatches > 0 ? (totalCompleted / totalMatches) * 100 : 0}%`,
            transition: 'width 0.3s'
          }} />
        </div>
      </div>

      {/* All Rounds - Scrollable List */}
      <div className="all-rounds-score-entry">
        {tournament.schedule.map((round, roundIndex) => (
          <div key={round.index} className="card mb-2 round-section">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '1rem', 
              flexWrap: 'wrap', 
              gap: '0.5rem',
              padding: '0.75rem',
              background: round.matches.every(m => m.isComplete) ? '#d4edda' : '#f8f9fa',
              borderRadius: '4px',
              borderLeft: `4px solid ${round.matches.every(m => m.isComplete) ? '#27ae60' : '#3498db'}`
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Round {round.index + 1}</h2>
              <span className="badge" style={{ 
                background: round.matches.every(m => m.isComplete) ? '#d4edda' : '#fff3cd',
                color: round.matches.every(m => m.isComplete) ? '#155724' : '#856404'
              }}>
                {getRoundProgress(roundIndex)} Complete
              </span>
            </div>

            <div className="matches-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
              {round.matches.map((match) => (
                <div
                  key={match.id}
                  className="card match-card"
                  style={{
                    background: match.isComplete ? '#d4edda' : 'white',
                    cursor: match.isComplete ? 'default' : 'pointer',
                    border: match.isComplete ? '2px solid #28a745' : '1px solid #ddd',
                    padding: '1rem',
                    transition: 'transform 0.1s, box-shadow 0.1s',
                    marginBottom: 0
                  }}
                  onClick={() => !match.isComplete && openScoreModal(match, roundIndex)}
                  onTouchStart={(e) => !match.isComplete && (e.currentTarget.style.transform = 'scale(0.98)')}
                  onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div className="flex flex-between mb-2">
                    <strong>Court {match.courtIndex + 1}</strong>
                    {match.isComplete ? (
                      <span className="badge" style={{ background: '#28a745', color: 'white' }}>
                        Done
                      </span>
                    ) : (
                      <span className="badge" style={{ background: '#ffc107', color: '#000' }}>
                        Tap to Score
                      </span>
                    )}
                  </div>

                  <div className="match-teams" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {/* Team A */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'rgba(52, 152, 219, 0.1)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{getPlayerName(match.teamA.player1Id)}</div>
                        <div style={{ fontWeight: 500 }}>{getPlayerName(match.teamA.player2Id)}</div>
                      </div>
                      {match.isComplete && (
                        <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{match.teamAScore}</span>
                      )}
                    </div>

                    {/* VS */}
                    <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#666', fontSize: '0.875rem' }}>
                      VS
                    </div>

                    {/* Team B */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'rgba(231, 76, 60, 0.1)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{getPlayerName(match.teamB.player1Id)}</div>
                        <div style={{ fontWeight: 500 }}>{getPlayerName(match.teamB.player2Id)}</div>
                      </div>
                      {match.isComplete && (
                        <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{match.teamBScore}</span>
                      )}
                    </div>
                  </div>

                  {match.isComplete && (
                    <div style={{ marginTop: '0.75rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {match.teamAScore} - {match.teamBScore}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {round.matches.length === 0 && (
              <p className="text-center">No matches in this round.</p>
            )}
          </div>
        ))}
      </div>

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          background: '#3498db',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          fontSize: '1.5rem',
          zIndex: 999
        }}
        title="Scroll to top"
      >
        ↑
      </button>

      {activeMatch && (
        <div 
          className="score-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto',
            padding: '1rem'
          }}
          onClick={closeScoreModal}
        >
          <div 
            className="card score-modal"
            style={{ 
              maxWidth: '500px', 
              width: '100%', 
              maxHeight: '90vh', 
              overflow: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Enter Score</h2>
            <p className="text-center" style={{ color: '#666', marginBottom: '1.5rem' }}>
              Court {activeMatch.courtIndex + 1} - Round {activeRoundIndex + 1}
            </p>

            {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

            <div className="score-entry-container" style={{ marginBottom: '1.5rem' }}>
              {/* Team A */}
              <div 
                className="team-score-input"
                style={{ 
                  background: 'rgba(52, 152, 219, 0.1)',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                  <strong style={{ fontSize: '1.1rem', color: '#2980b9' }}>Team A</strong>
                </div>
                <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                  {getPlayerName(activeMatch.teamA.player1Id)} & {getPlayerName(activeMatch.teamA.player2Id)}
                </div>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={teamAScore}
                  onChange={(e) => setTeamAScore(e.target.value)}
                  style={{ 
                    width: '100%', 
                    fontSize: '1.5rem', 
                    textAlign: 'center',
                    padding: '0.75rem'
                  }}
                  autoFocus
                  placeholder="Score"
                />
              </div>

              {/* VS */}
              <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#666', margin: '0.5rem 0' }}>
                VS
              </div>

              {/* Team B */}
              <div 
                className="team-score-input"
                style={{ 
                  background: 'rgba(231, 76, 60, 0.1)',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginTop: '1rem'
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                  <strong style={{ fontSize: '1.1rem', color: '#c0392b' }}>Team B</strong>
                </div>
                <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                  {getPlayerName(activeMatch.teamB.player1Id)} & {getPlayerName(activeMatch.teamB.player2Id)}
                </div>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={teamBScore}
                  onChange={(e) => setTeamBScore(e.target.value)}
                  style={{ 
                    width: '100%', 
                    fontSize: '1.5rem', 
                    textAlign: 'center',
                    padding: '0.75rem'
                  }}
                  placeholder="Score"
                />
              </div>
            </div>

            <div className="flex gap-1" style={{ flexWrap: 'wrap' }}>
              <button
                className="btn-secondary"
                onClick={closeScoreModal}
                disabled={submitting}
                style={{ flex: 1, minWidth: '120px' }}
              >
                Cancel
              </button>
              <button
                className="btn-success"
                onClick={handleSubmitScore}
                disabled={submitting}
                style={{ flex: 1, minWidth: '120px' }}
              >
                {submitting ? 'Saving...' : 'Save Score'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
